import React, { useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import '../styles/Services.css';
// Import only the background image we know exists
import servicesBg from '../assets/images/services-bg.jpg';
import gesellschaft from '../assets/images/gesellschaft.jpg';
import haushalt from '../assets/images/haushalt.JPG';
import unterstuetzung from '../assets/images/unterstuetzung.JPG';

const Services = () => {
  const detailsRefs = {
    gesellschaft: useRef(null),
    haushaltshilfe: useRef(null),
    begleitdienste: useRef(null),
    unterstuetzung: useRef(null)
  };

  const scrollToDetails = (sectionId, event) => {
    // Remove focus from the button and prevent default behavior
    if (event) {
      event.preventDefault();
      if (event.currentTarget) {
        event.currentTarget.blur();
      }
      
      // Add a small delay before scrolling to ensure any hover/active states are reset
      setTimeout(() => {
        const section = detailsRefs[sectionId].current;
        if (section) {
          // Get the position of the element relative to the viewport
          const rect = section.getBoundingClientRect();
          // Calculate the absolute position by adding the scroll position
          const absoluteTop = rect.top + window.pageYOffset;
          
          window.scrollTo({
            top: absoluteTop - 120, // Adjusted offset for better positioning
            behavior: 'smooth'
          });
        }
      }, 10);
    }
  };

  return (
    <>
      <Helmet>
        <title>Leistungen | Alltagsbegleitung Jena – Catrin Kaßner</title>
        <meta name="description" content="Gesellschaft, Haushaltshilfe, Begleitdienste und Behördenunterstützung für Senioren in Jena und Umgebung. Individuelle Alltagsbegleitung von Catrin Kaßner." />
        <link rel="canonical" href="https://www.alltagsbegleitung-ckassner.de/services" />
      </Helmet>
      <div className="page-background services-background"></div>
      <div className="services">
        <h1>Meine Leistungen in Jena und Umgebung</h1>
        <div className="services-grid">
          <div className="service-card">
            <h2>Gesellschaft</h2>
            <ul>
              <li>Regelmäßige Besuche und Gespräche</li>
              <li>Gesellschaftsspiele und Aktivitäten</li>
              <li>Vorlesen und Unterhaltung</li>
            </ul>
            <button 
              className="mehr-lesen-btn" 
              onClick={(e) => scrollToDetails('gesellschaft', e)}
            >
              Mehr lesen <i className="fas fa-chevron-down"></i>
            </button>
          </div>
          <div className="service-card">
            <h2>Unterstützung</h2>
            <ul>
              <li>Antragstellungen bei <br/>Ämtern, Behörden, etc...</li>
            </ul>
            <button 
              className="mehr-lesen-btn" 
              onClick={(e) => scrollToDetails('unterstuetzung', e)}
            >
              Mehr lesen <i className="fas fa-chevron-down"></i>
            </button>
          </div>
          <div className="service-card">
            <h2>Begleitdienste</h2>
            <ul>
              <li>Einkaufen</li>
              <li>Behörden</li>
              <li>Arzt</li>
              <li>Friseur</li>
              <li>Spaziergänge</li>
            </ul>
            <button 
              className="mehr-lesen-btn" 
              onClick={(e) => scrollToDetails('begleitdienste', e)}
            >
              Mehr lesen <i className="fas fa-chevron-down"></i>
            </button>
          </div>
          <div className="service-card">
            <h2>Haushaltshilfe</h2>
            <ul>
              <li>Reinigungsarbeiten im Haushalt</li>
              <li>Kochen / Backen</li>
              <li>Kleine Gartenarbeiten</li>
              <li>Kleine Reparaturen</li>
            </ul>
            <button 
              className="mehr-lesen-btn" 
              onClick={(e) => scrollToDetails('haushaltshilfe', e)}
            >
              Mehr lesen <i className="fas fa-chevron-down"></i>
            </button>
          </div>
        </div>
        
        <div className="service-details-container">
          <div className="service-details" ref={detailsRefs.gesellschaft} id="gesellschaft-details">
            <h2>Gesellschaft im Detail</h2>
            <div className="service-details-content">
              <div className="service-details-image">
                {/* Just use the services background for all sections */}
                <img src={gesellschaft} alt="Alltagsbegleiterin Catrin Kaßner im Gespräch mit Senior in Jena" />
              </div>
              <div className="service-details-text">
                <p>Einsamkeit im Alter ist ein häufiges Problem. Meine Betreuungsleistungen bieten regelmäßige Gesellschaft durch:</p>
                <ul>
                  <li>Regelmäßige Besuche mit Zeit für persönliche Gespräche</li>
                  <li>Gemeinsames Spielen von Gesellschaftsspielen</li>
                  <li>Vorlesen aus Büchern, Zeitungen oder Zeitschriften</li>
                  <li>Gemeinsame Aktivitäten wie Fotoalben anschauen</li>
                  <li>Erinnerungsarbeit und biografisches Erzählen</li>
                </ul>
                <p>Mein Ziel ist es, eine vertrauensvolle Beziehung aufzubauen und für Freude im Alltag zu sorgen.</p>
              </div>
            </div>
          </div>

          <div className="service-details" ref={detailsRefs.unterstuetzung} id="unterstuetzung-details">
            <h2>Unterstützung im Detail</h2>
            <div className="service-details-content">
              <div className="service-details-image">
                <img src={unterstuetzung} alt="Unterstützung bei Behördengängen für Senioren in Jena" />
              </div>
              <div className="service-details-text">
                <p>Bürokratische Angelegenheiten können im Alter schnell überfordernd sein. Ich unterstütze bei:</p>
                <ul>
                  <li>Antragstellungen bei Ämtern und Behörden</li>
                  <li>Hilfe beim Ausfüllen von Formularen</li>
                  <li>Beratung zu Pflegegraden und Leistungen</li>
                  <li>Unterstützung bei der Kommunikation mit Versicherungen</li>
                  <li>Organisation und Sortieren von wichtigen Dokumenten</li>
                  <li>Hilfe bei der Beantragung von Hilfsmitteln</li>
                </ul>
                <p>Mit meiner Hilfe behalten Sie den Überblick über wichtige administrative Angelegenheiten.</p>
              </div>
            </div>
          </div>

          <div className="service-details" ref={detailsRefs.begleitdienste} id="begleitdienste-details">
            <h2>Begleitdienste im Detail</h2>
            <div className="service-details-content">
              <div className="service-details-image">
                <img src={servicesBg} alt="Begleitdienste für Senioren in Jena – Arztbegleitung, Einkaufen und Spaziergänge" />
              </div>
              <div className="service-details-text">
                <p>Mobil zu bleiben und am öffentlichen Leben teilzunehmen ist wichtig für das Wohlbefinden. Meine Begleitdienste umfassen:</p>
                <ul>
                  <li>Einkaufsbegleitung im Supermarkt oder anderen Geschäften</li>
                  <li>Begleitung und Unterstützung bei Behördengängen</li>
                  <li>Begleitung zu Arztbesuchen und Therapieterminen</li>
                  <li>Friseur-, Fußpflege- oder andere Wellnesstermine</li>
                  <li>Gemeinsame Spaziergänge für Bewegung und frische Luft</li>
                  <li>Begleitung zu kulturellen Veranstaltungen oder Familientreffen</li>
                </ul>
                <p>Ich sorge für Sicherheit unterwegs und eine angenehme Begleitung bei allen Aktivitäten außer Haus.</p>
              </div>
            </div>
          </div>

          <div className="service-details" ref={detailsRefs.haushaltshilfe} id="haushaltshilfe-details">
            <h2>Haushaltshilfe im Detail</h2>
            <div className="service-details-content">
              <div className="service-details-image">
                <img src={haushalt} alt="Haushaltshilfe für Senioren in Jena – Reinigung, Kochen und Gartenarbeit" />
              </div>
              <div className="service-details-text">
                <p>Mit zunehmendem Alter können alltägliche Haushaltsarbeiten zur Herausforderung werden. Ich biete Unterstützung bei:</p>
                <ul>
                  <li>Regelmäßige Reinigungsarbeiten wie Staubsaugen, Wischen und Staubwischen</li>
                  <li>Wäsche waschen, aufhängen und zusammenlegen</li>
                  <li>Kochen von Mahlzeiten nach persönlichen Vorlieben</li>
                  <li>Backen von Kuchen oder anderen Leckereien</li>
                  <li>Pflege von Zimmerpflanzen und kleine Gartenarbeiten</li>
                  <li>Kleine Reparaturen im Haushalt</li>
                </ul>
                <p>Diese Hilfe ermöglicht es Ihnen oder Ihren Angehörigen, länger selbstständig in der gewohnten Umgebung zu leben.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Services;
